import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { endpoints, baseUrl } from '../../api/api';
import AdminHeader from './components/AdminHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CoverType {
  type: string;
  count: number;
  pct: number;
}

interface AddonData {
  totalBooks: number;
  addons: {
    spellcheck:  { count: number; pct: number };
    coverDesign: { count: number; pct: number };
    coverTypes:  CoverType[];
  };
  trend: {
    month: string;
    label: string;
    total: number;
    spellcheck: number;
    coverDesign: number;
  }[];
}

interface AICoverInsights {
  totalGenerations: number;
  booksUsed: number;
  booksAtLimit: number;
  generationsLimit: number;
  distribution: Record<number, number>;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const AdoptionCard: React.FC<{
  label: string;
  count: number;
  pct: number;
  total: number;
  color: string;
  description: string;
}> = ({ label, count, pct, total, color, description }) => (
  <div className="bg-white rounded-lg border border-gray-200 px-5 py-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <div className="mt-2 flex items-end gap-2">
      <span className="text-3xl font-bold text-gray-900">{pct}%</span>
      <span className="text-sm text-gray-400 mb-0.5">of books</span>
    </div>
    <p className="text-xs text-gray-400 mt-0.5">{count.toLocaleString()} of {total.toLocaleString()} books</p>
    <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
    <p className="text-xs text-gray-400 mt-2">{description}</p>
  </div>
);

const COVER_TYPE_META: Record<string, { label: string; color: string }> = {
  minimalistic: { label: 'Minimalistic', color: 'bg-violet-400' },
  photography:  { label: 'Photography',  color: 'bg-blue-400'   },
  typography:   { label: 'Typography',   color: 'bg-teal-400'   },
  custom:       { label: 'Custom / None', color: 'bg-gray-300'  },
};

const CoverTypeBreakdown: React.FC<{ types: CoverType[]; total: number }> = ({ types, total }) => {
  const maxCount = Math.max(...types.map(t => t.count), 1);
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-5 py-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Cover Design Type</p>
      <p className="text-xs text-gray-400 mb-4">Breakdown among {total.toLocaleString()} books using the cover design add-on</p>
      <div className="space-y-3">
        {types.map(t => {
          const meta = COVER_TYPE_META[t.type] || { label: t.type, color: 'bg-gray-300' };
          return (
            <div key={t.type}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">{meta.label}</span>
                <span className="text-xs text-gray-500">
                  {t.count.toLocaleString()} <span className="text-gray-400">({t.pct}%)</span>
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${meta.color}`}
                  style={{ width: `${(t.count / maxCount) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function trendPct(count: number, total: number): string {
  if (!total) return '—';
  return `${Math.round((count / total) * 100)}%`;
}

const TrendTable: React.FC<{ trend: AddonData['trend'] }> = ({ trend }) => {
  if (trend.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-6">No trend data yet</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Books Created</th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Spellcheck</th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Cover Design</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {trend.map(row => (
            <tr key={row.month} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-800">{row.label}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.total.toLocaleString()}</td>
              <td className="px-4 py-3 text-right">
                <span className="text-gray-800">{row.spellcheck}</span>
                <span className="text-gray-400 text-xs ml-1">({trendPct(row.spellcheck, row.total)})</span>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="text-gray-800">{row.coverDesign}</span>
                <span className="text-gray-400 text-xs ml-1">({trendPct(row.coverDesign, row.total)})</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]               = useState<AddonData | null>(null);
  const [aiData, setAiData]           = useState<AICoverInsights | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [addonRes, aiRes] = await Promise.all([
        axios.get(`${baseUrl}${endpoints.adminGetAddonAdoption}`, { headers }),
        axios.get(`${baseUrl}/api/analytics/admin/ai-cover-insights`, { headers }),
      ]);
      setData(addonRes.data);
      setAiData(aiRes.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'admin') { navigate('/'); return; }
      } catch { navigate('/'); return; }
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} />

      <main className="px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add-on Adoption</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {data ? `Across ${data.totalBooks.toLocaleString()} books total` : 'Feature usage across all books'}
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-white rounded-lg shadow px-6 py-10 text-center">
            <p className="text-sm text-red-500">Failed to load analytics data.</p>
            <button onClick={fetchData} className="mt-3 text-sm text-blue-600 hover:underline">Try again</button>
          </div>
        )}

        {data && !loading && (
          <>
            {/* Adoption rate cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AdoptionCard
                label="Spellcheck"
                count={data.addons.spellcheck.count}
                pct={data.addons.spellcheck.pct}
                total={data.totalBooks}
                color="bg-emerald-400"
                description="Authors who enabled grammar & spell checking"
              />
              <AdoptionCard
                label="Cover Design"
                count={data.addons.coverDesign.count}
                pct={data.addons.coverDesign.pct}
                total={data.totalBooks}
                color="bg-violet-400"
                description="Authors who used the AI cover design add-on"
              />
            </div>

            {/* Cover type breakdown */}
            {data.addons.coverDesign.count > 0 && (
              <CoverTypeBreakdown
                types={data.addons.coverTypes}
                total={data.addons.coverDesign.count}
              />
            )}

            {/* Monthly trend */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-800">Monthly Trend</p>
                <p className="text-xs text-gray-400 mt-0.5">Add-on usage rate per month — last 6 months</p>
              </div>
              <TrendTable trend={data.trend} />
            </div>

            {/* AI Cover Generation Insights */}
            {aiData && (
              <div className="bg-white rounded-lg border border-gray-200 px-5 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-gray-800">AI Cover Generation</p>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 uppercase tracking-wide">Beta · {aiData.generationsLimit}/book</span>
                </div>
                <p className="text-xs text-gray-400 mb-4">Usage across all books — limited-time free offer (2 generations per book)</p>

                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{aiData.totalGenerations.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Total generations</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{aiData.booksUsed.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Books used AI</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{aiData.booksAtLimit.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-0.5">At limit (quota full)</p>
                  </div>
                </div>

                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Quota usage distribution</p>
                <div className="space-y-2">
                  {[0, 1, 2].map(n => {
                    const count = aiData.distribution[n] ?? 0;
                    const total = Object.values(aiData.distribution).reduce((a, b) => a + b, 0);
                    const pct = total ? Math.round((count / total) * 100) : 0;
                    const labels = ['Unused (0 generations)', '1 generation used', '2 generations used (limit)'];
                    const colors = ['bg-gray-200', 'bg-orange-300', 'bg-orange-500'];
                    return (
                      <div key={n}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-gray-600">{labels[n]}</span>
                          <span className="text-xs text-gray-500">{count.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${colors[n]}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AnalyticsDashboard;